import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatDate } from "@/lib/format";
import { Star, MessageSquare, Send, Pin, CheckCircle } from "lucide-react";

const SERVICES = [
  "LostDC Hosting",
  "LostDC Network",
  "LostDRKC",
  "Casper Wearables",
  "LostDC Projects",
  "General",
];

export default function CustomerFeedback() {
  const [items, setItems] = useState([]);
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [service, setService] = useState(SERVICES[0]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/customer/feedback");
      setItems(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (comment.trim().length < 3) return toast.error("Write a bit more, please");
    setBusy(true);
    try {
      await api.post("/customer/feedback", { comment: comment.trim(), rating: rating || null, service });
      toast.success("Thanks — your feedback is awaiting admin approval.");
      setComment(""); setRating(0);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Community" title="Feedback & Reviews" />

      <div className="grid lg:grid-cols-5 gap-4">
        <form onSubmit={submit} className="ldc-glass p-6 lg:col-span-2 self-start" data-testid="feedback-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-md bg-[#fbce18]/10 border border-[#fbce18]/30 flex items-center justify-center text-[#fbce18]"><MessageSquare size={16}/></div>
            <h3 className="font-display text-lg">Share your feedback</h3>
          </div>

          <label className="block mb-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Service</div>
            <select value={service} onChange={(e) => setService(e.target.value)} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="feedback-service">
              {SERVICES.map(s => (<option key={s} value={s}>{s}</option>))}
            </select>
          </label>

          <label className="block mb-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Rating (optional)</div>
            <div className="flex gap-1" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n === rating ? 0 : n)}
                  className="p-1 hover:scale-110 transition-transform"
                  data-testid={`feedback-star-${n}`}
                  aria-label={`${n} star`}
                >
                  <Star size={22} fill={n <= rating ? "#fbce18" : "transparent"} color={n <= rating ? "#fbce18" : "rgba(255,255,255,0.35)"} />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 text-sm text-white/60 self-center">{rating} / 5</span>}
            </div>
          </label>

          <label className="block mb-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Your comment</div>
            <textarea
              rows={5}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us what you liked (or didn't). Approved reviews may show up on our homepage."
              className="ldc-input w-full rounded-md px-3 py-2 text-sm"
              maxLength={1000}
              data-testid="feedback-comment"
            />
            <div className="text-[10px] text-white/40 mt-1 text-right">{comment.length}/1000</div>
          </label>

          <button type="submit" disabled={busy} className="ldc-btn-primary w-full py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2" data-testid="feedback-submit">
            <Send size={14}/> {busy ? "Submitting…" : "Submit feedback"}
          </button>
          <div className="mt-3 text-[11px] text-white/45">
            Feedback goes into a moderation queue. Once approved by an admin, it can appear on the LostDRKC homepage.
          </div>
        </form>

        <div className="lg:col-span-3">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80 mb-2">Your submissions</div>
          <div className="space-y-3" data-testid="feedback-my-list">
            {items.length === 0 && (
              <div className="ldc-glass p-8 text-center text-white/50">
                You haven't submitted feedback yet. Share your experience above.
              </div>
            )}
            {items.map((f) => (
              <div key={f.id} className="ldc-glass p-5" data-testid={`feedback-my-${f.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {typeof f.rating === "number" && (
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} size={13} fill={i < f.rating ? "#fbce18" : "transparent"} color={i < f.rating ? "#fbce18" : "rgba(255,255,255,0.25)"} />
                          ))}
                        </div>
                      )}
                      <span className="text-xs text-white/50">{f.service}</span>
                    </div>
                    <div className="mt-2 text-sm text-white/85">{f.comment}</div>
                    <div className="mt-2 text-[11px] text-white/40">{formatDate(f.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={f.status} />
                    {f.pinned && <div className="mt-2 text-[10px] text-[#fbce18] inline-flex items-center gap-1"><Pin size={10}/> Pinned</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "approved") return <span className="ldc-badge ldc-badge-paid"><CheckCircle size={10}/> Approved</span>;
  if (status === "rejected") return <span className="ldc-badge ldc-badge-failed">Rejected</span>;
  return <span className="ldc-badge ldc-badge-pending">Pending</span>;
}
